import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentSite, resolveLastSite } from '@/contexts/SiteContext';
import { SiteSwitcherMenu } from '@/components/layout/SiteSwitcherMenu';
import { Tooltip } from '@/components/shared/Tooltip';
import { IconChevronDown } from '@/lib/icons';
import type { Site } from '@/types';

// SiteSwitcher is the persistent control at the top of the workspace nav
// column. It shows the active site (or the sticky last-active site when the
// operator is on a global route) as name + /slug + a selector chevron, and
// opens the shared SiteSwitcherMenu on click. With zero sites it renders a
// dashed "Select a site" empty state that routes to the /sites lobby.
//
// Click behavior mirrors the breadcrumb switcher (the agreed shared logic):
//   - more than one selectable site -> open the dropdown
//   - exactly one site               -> go straight to its hub
//   - zero sites                     -> go to /sites to create one
//
// Selection routes through SiteContext.setSite so the unsaved-edits guard
// (aeo:unsaved-site-switch) still fires.

interface SiteSwitcherProps {
  collapsed: boolean;
}

export function SiteSwitcher({ collapsed }: SiteSwitcherProps) {
  const { currentSite, allSites, setSite } = useCurrentSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // The displayed site is the active one, or the sticky fallback on global
  // routes. resolveLastSite is the SAME resolver RootRoute uses, so the
  // column and `/` always agree on which site is "current".
  const target = useMemo<Site | null>(
    () => currentSite ?? resolveLastSite(allSites),
    [currentSite, allSites],
  );
  const visibleSites = useMemo<Site[]>(
    () => allSites.filter((s) => s.status !== 'decommissioned'),
    [allSites],
  );
  const showChevron = visibleSites.length > 1;

  const onTriggerClick = useCallback(() => {
    if (!target) {
      navigate('/sites');
      return;
    }
    if (!showChevron) {
      navigate(`/sites/${target.slug}`);
      return;
    }
    setOpen((v) => !v);
  }, [target, showChevron, navigate]);

  const handleSelect = useCallback(
    (slug: string) => {
      if (slug !== currentSite?.slug) setSite(slug);
      setOpen(false);
    },
    [currentSite, setSite],
  );

  const handleManageSites = useCallback(() => {
    setOpen(false);
    navigate('/sites');
  }, [navigate]);

  // Empty state: no sites at all. Dashed, muted, routes to the lobby.
  if (!target) {
    const empty = (
      <button
        ref={triggerRef}
        type="button"
        onClick={() => navigate('/sites')}
        className={`flex items-center gap-2 w-full rounded-md border border-dashed border-dash-border text-dash-muted hover:text-dash-secondary hover:border-dash-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
          collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
        }`}
        aria-label="Select a site"
      >
        {collapsed ? (
          <span className="text-sm font-mono">+</span>
        ) : (
          <span className="text-sm truncate">Select a site</span>
        )}
      </button>
    );
    return collapsed ? (
      <Tooltip placement="right" content="Select a site">
        {empty}
      </Tooltip>
    ) : (
      empty
    );
  }

  const initial = target.name.trim().charAt(0).toUpperCase() || '?';

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      onClick={onTriggerClick}
      className={`flex items-center gap-2 w-full rounded-md border border-dash-border bg-dash-bg hover:border-dash-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 ${
        collapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'
      }`}
      aria-haspopup={showChevron ? 'listbox' : undefined}
      aria-expanded={showChevron ? open : undefined}
      aria-label={`Current site: ${target.name}. Switch site.`}
    >
      {collapsed ? (
        <span className="w-6 h-6 rounded bg-accent/15 text-accent text-xs font-semibold flex items-center justify-center">
          {initial}
        </span>
      ) : (
        <>
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-semibold text-dash-primary truncate">
              {target.name}
            </div>
            <div className="text-[10px] font-mono text-dash-muted truncate">
              /{target.slug}
            </div>
          </div>
          {showChevron ? (
            <IconChevronDown size={14} className="shrink-0 text-dash-muted" aria-hidden="true" />
          ) : null}
        </>
      )}
    </button>
  );

  return (
    <>
      {collapsed ? (
        <Tooltip placement="right" content={target.name}>
          {trigger}
        </Tooltip>
      ) : (
        trigger
      )}
      {showChevron ? (
        <SiteSwitcherMenu
          open={open}
          onClose={() => setOpen(false)}
          sites={visibleSites}
          currentSlug={target.slug}
          onSelect={handleSelect}
          onManageSites={handleManageSites}
          triggerRef={triggerRef}
        />
      ) : null}
    </>
  );
}
